/**
 * data-layer-thirdparty.client.ts
 *
 * Bridges events pushed onto `window.datalayer` by third-party scripts into
 * the CPP nhstDataLayer (legacy) AND @nhst/tracking-lib (new).
 *
 * Previously this plugin used:
 *   window.datalayer = ref(window.datalayer || [])
 *   watch(window.datalayer, ..., { immediate: true, deep: true })
 *
 * `deep: true` forced Vue to install a reactive proxy across every queued
 * event object on every push, and `immediate: true` ran the drain callback
 * once on mount when the array was always empty. Both were wasted work.
 *
 * The new implementation uses a thin Proxy on the underlying Array and
 * drains synchronously on `.push()`, with no reactivity involved at all.
 */
import { useDataLayerEvent } from '~/composables/useDataLayerEvent';
import { legacyToContract, trackContract } from '~/utils/payloadShape';

interface ThirdpartyEvent {
  event?: string;
  eventName?: string;
  eventInfo?: any;
}

function drain (queue: ThirdpartyEvent[]): void {
  // splice(0, length) preserves chronological order — `.pop()` would reverse it.
  const items = queue.splice(0, queue.length);
  for (const ev of items) {
    if (ev?.event === 'USER_ACTION' && ev.eventName) {
      // Legacy: nhstDataLayer → Adobe Satellite (unchanged)
      useDataLayerEvent().userEvent(ev.eventName, ev.eventInfo);
      /*
       * New: @nhst/tracking-lib — contract-shaped via legacyToContract.
       * External pushes carry the legacy { action, content } eventInfo shape;
       * we flatten it into a single bag the mapper can split correctly.
       */
      const bag = {
        eventName: ev.eventName,
        ...(ev.eventInfo?.action || {}),
        ...(ev.eventInfo?.content || {})
      };
      trackContract({ event: 'user_action', ...legacyToContract(bag) });
    }
  }
}

export default defineNuxtPlugin(() => {
  const existing: ThirdpartyEvent[] = Array.isArray(window.datalayer) ? window.datalayer.slice() : [];

  /*
   * Replace window.datalayer with a Proxy so any `push` / `[i] =` write
   * triggers a drain without involving Vue reactivity.
   */
  const proxied = new Proxy<ThirdpartyEvent[]>([], {
    set (target, prop, value) {
      (target as any)[prop] = value;
      /*
       * Only schedule a drain on `length` writes. `.push` writes the new item
       * at the numeric index first and then `length` last, so this fires
       * exactly once per push (the index write is a no-op signal here).
       */
      if (prop === 'length') {
        /*
         * Defer to a microtask so the entire .push() / spread call completes
         * before we drain — protects against re-entrant pushes mid-iteration.
         */
        queueMicrotask(() => drain(target));
      }
      return true;
    }
  });

  // Replay anything that was already queued before the plugin loaded.
  for (const ev of existing) { proxied.push(ev); }

  window.datalayer = proxied as any;
});
